import { type ComponentProps, type PartData } from '@enonic/react-components'
import TwoColumnFormPrototype from '/react4xp/components/prototype/TwoColumnForm'
import type { TwoColumnFormData, TwoColumnFormLabels } from '/react4xp/components/prototype/TwoColumnForm/types'

/**
 * Adapter for the styled prototype Two Column Form: the markup and styling all
 * come from the prototype component, this View supplies the Enonic data and
 * posts to the two-column-form service.
 */
type TwoColumnFormPartData = {
	submitUrl?: string
	contentId?: string
	componentPath?: string
	headline?: string
	intro?: string
	image?: { src: string; alt?: string }
	formTitle?: string
	// `resolveLink` returns either undefined or a complete link.
	privacyLink?: { label: string; url: string; isExternal: boolean }
	tooltipLabel?: string
	labels?: TwoColumnFormLabels
}

export const TwoColumnForm = ({ data }: ComponentProps<PartData>) => {
	const { submitUrl, contentId, componentPath, headline, intro, image, formTitle, privacyLink, tooltipLabel, labels } =
		(data || {}) as TwoColumnFormPartData

	const handleSubmit = async (values: TwoColumnFormData) => {
		// Sent as urlencoded rather than multipart so the service can read the
		// fields straight off `request.params`.
		const body = new URLSearchParams()
		body.set('name', values.name)
		body.set('email', values.email)
		body.set('location', values.location || '')
		body.set('message', values.message || '')
		// Unlike ContactForm.tsx, this prototype's Checkbox stores a genuine
		// 'true'/'' string via `updateFormValue(e.target.checked ? 'true' : '')`,
		// so `TwoColumnFormData.consent` is a real true/false - forward it as-is
		// rather than hardcoding 'true', or an unticked box would be silently
		// accepted server-side.
		body.set('consent', String(values.consent))
		if (contentId) body.set('contentId', contentId)
		if (componentPath) body.set('componentPath', componentPath)
		body.set('pageUrl', window.location.href)

		// The prototype form treats a throw as its error path, so failures must
		// not be returned as a status.
		const response = await fetch(submitUrl!, {
			method: 'POST',
			headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
			body: body.toString(),
		})
		if (!response.ok) {
			throw new Error(`Two Column Form submission failed: ${response.status}`)
		}
	}

	return (
		<TwoColumnFormPrototype
			headline={headline}
			intro={intro}
			image={image}
			formTitle={formTitle}
			privacyLink={privacyLink && { url: privacyLink.url, isExternal: privacyLink.isExternal }}
			tooltipLabel={tooltipLabel}
			labels={labels}
			onSubmit={handleSubmit}
		/>
	)
}
